import { SOUND_PACKS, THEME_PRESETS, applyThemePreset, getSoundPack } from './catalog';
import type {
  TableArtSlot,
  TableDefinition,
  TableSoundEvent,
} from '../engine/tables/definition';

// Keep the deliberately ridiculous pack in the same shared catalog used by
// Creator Studio without adding downloadable user audio or moderation risk.
if (!SOUND_PACKS.some((pack) => pack.id === 'silly')) {
  SOUND_PACKS.push({
    id: 'silly',
    name: 'Silly',
    description: 'Farts, barks, slide whistles, rubber chickens and mischievous cat noises.',
    character: 'cartoon',
    pitch: 1,
    brightness: 0.9,
    decay: 0.7,
  });
}

export type SoundEffectId =
  | 'coil-clack'
  | 'coil-heavy'
  | 'escapement-tick'
  | 'sonar-return'
  | 'telemetry-uplink'
  | 'reactor-breaker'
  | 'spectral-capture'
  | 'dragon-breath'
  | 'temple-heart'
  | 'pit-service'
  | 'steam-brake'
  | 'pop-chime'
  | 'pop-deep'
  | 'rubber-snap'
  | 'target-knock'
  | 'target-zap'
  | 'lane-ping'
  | 'lane-bleep'
  | 'ramp-rise'
  | 'ramp-bell'
  | 'plunger-spring'
  | 'nudge-thump'
  | 'drain-drop'
  | 'save-rise'
  | 'tilt-buzz'
  | 'start-fanfare'
  | 'gameover-drop'
  | 'jackpot-fanfare'
  | 'boing'
  | 'fart-pop'
  | 'dog-bark'
  | 'slide-whistle'
  | 'chicken-squawk'
  | 'cat-meow'
  | 'cat-hiss'
  | 'cat-purr';

export interface SoundEffectAsset {
  id: SoundEffectId;
  name: string;
  family: 'mechanical' | 'electronic' | 'musical' | 'utility' | 'silly';
  description: string;
}

export const SOUND_EFFECTS: SoundEffectAsset[] = [
  { id: 'coil-clack', name: 'Coil Clack', family: 'mechanical', description: 'Fast flipper-solenoid snap.' },
  { id: 'coil-heavy', name: 'Heavy Coil', family: 'mechanical', description: 'Deeper cabinet coil impact.' },
  { id: 'escapement-tick', name: 'Escapement Tick', family: 'mechanical', description: 'Paired clockwork pallet-and-gear ticks.' },
  { id: 'sonar-return', name: 'Sonar Return', family: 'electronic', description: 'Bright sonar transmit ping followed by a delayed deep-water echo.' },
  { id: 'telemetry-uplink', name: 'Telemetry Uplink', family: 'electronic', description: 'Three-stage carrier handshake that climbs into a compact data burst.' },
  { id: 'reactor-breaker', name: 'Reactor Breaker', family: 'electronic', description: 'High-voltage contactor trip with a sharp electrical arc and collapsing bus tone.' },
  { id: 'spectral-capture', name: 'Spectral Capture', family: 'musical', description: 'Airy spirit draw that rises into a cold descending resonance.' },
  { id: 'dragon-breath', name: 'Dragon Breath', family: 'musical', description: 'Low scaled growl blooming into a short fiery breath burst.' },
  { id: 'temple-heart', name: 'Temple Heart', family: 'musical', description: 'Stone-heart pulse blooming into an ancient crystalline resonance.' },
  { id: 'pit-service', name: 'Pit Service', family: 'mechanical', description: 'Pneumatic impact-wrench chatter followed by a short pit-lane air release.' },
  { id: 'steam-brake', name: 'Steam Brake', family: 'mechanical', description: 'Heavy rail coupler clank venting into a short brake-line hiss.' },
  { id: 'pop-chime', name: 'Pop Chime', family: 'musical', description: 'Bright classic pop-bumper ring.' },
  { id: 'pop-deep', name: 'Deep Pop', family: 'mechanical', description: 'Lower, punchier bumper kick.' },
  { id: 'rubber-snap', name: 'Rubber Snap', family: 'mechanical', description: 'Short elastic slingshot crack.' },
  { id: 'target-knock', name: 'Target Knock', family: 'mechanical', description: 'Dry stand-up target impact.' },
  { id: 'target-zap', name: 'Target Zap', family: 'electronic', description: 'Electronic target hit.' },
  { id: 'lane-ping', name: 'Lane Ping', family: 'musical', description: 'Short rollover bell.' },
  { id: 'lane-bleep', name: 'Lane Bleep', family: 'electronic', description: 'Retro solid-state lane tone.' },
  { id: 'ramp-rise', name: 'Ramp Rise', family: 'electronic', description: 'Ascending ramp travel sweep.' },
  { id: 'ramp-bell', name: 'Ramp Bell', family: 'musical', description: 'Rewarding ramp completion bell.' },
  { id: 'plunger-spring', name: 'Plunger Spring', family: 'mechanical', description: 'Shooter rod and spring release.' },
  { id: 'nudge-thump', name: 'Cabinet Nudge', family: 'mechanical', description: 'Low cabinet shove.' },
  { id: 'drain-drop', name: 'Drain Drop', family: 'utility', description: 'Falling end-of-ball tone.' },
  { id: 'save-rise', name: 'Ball Save', family: 'musical', description: 'Quick rescue flourish.' },
  { id: 'tilt-buzz', name: 'Tilt Buzz', family: 'utility', description: 'Harsh tilt warning buzz.' },
  { id: 'start-fanfare', name: 'Game Start', family: 'musical', description: 'Short start-of-game fanfare.' },
  { id: 'gameover-drop', name: 'Game Over', family: 'utility', description: 'Descending game-over cue.' },
  { id: 'jackpot-fanfare', name: 'Jackpot', family: 'musical', description: 'Three-stage jackpot flourish.' },
  { id: 'boing', name: 'Boing!', family: 'silly', description: 'Springy cartoon impact.' },
  { id: 'fart-pop', name: 'Pffft!', family: 'silly', description: 'An unapologetic tiny fart pop.' },
  { id: 'dog-bark', name: 'Woof!', family: 'silly', description: 'A synthetic two-part dog bark.' },
  { id: 'slide-whistle', name: 'Slide Whistle', family: 'silly', description: 'Long rising and falling whistle.' },
  { id: 'chicken-squawk', name: 'Rubber Chicken', family: 'silly', description: 'Squeaky synthetic chicken squawk.' },
  { id: 'cat-meow', name: 'Pin Meow', family: 'silly', description: 'Pin gives a dramatic electronic meow.' },
  { id: 'cat-hiss', name: 'Pin Hiss', family: 'silly', description: 'Noisy cat hiss for danger and tilt.' },
  { id: 'cat-purr', name: 'Pin Purr', family: 'silly', description: 'Low pulsing happy-cat reward.' },
];

const classicMap: Record<TableSoundEvent, SoundEffectId> = {
  'game-start': 'start-fanfare', flipper: 'coil-clack', bumper: 'pop-chime',
  slingshot: 'rubber-snap', target: 'target-knock', rollover: 'lane-ping',
  ramp: 'ramp-bell', plunger: 'plunger-spring', nudge: 'nudge-thump',
  drain: 'drain-drop', 'ball-save': 'save-rise', tilt: 'tilt-buzz',
  'game-over': 'gameover-drop', jackpot: 'jackpot-fanfare',
  'prop-appear': 'save-rise', 'prop-destroy': 'jackpot-fanfare',
};

const electronicMap: Record<TableSoundEvent, SoundEffectId> = {
  ...classicMap,
  flipper: 'coil-heavy', bumper: 'pop-deep', target: 'target-zap', rollover: 'lane-bleep',
  ramp: 'ramp-rise', 'prop-appear': 'target-zap', 'prop-destroy': 'ramp-rise',
};

const fantasyMap: Record<TableSoundEvent, SoundEffectId> = {
  ...classicMap,
  bumper: 'pop-chime', target: 'lane-ping', rollover: 'lane-ping', ramp: 'ramp-bell',
  'prop-appear': 'save-rise', 'prop-destroy': 'jackpot-fanfare',
};

const cartoonMap: Record<TableSoundEvent, SoundEffectId> = {
  ...classicMap,
  'game-start': 'slide-whistle', flipper: 'boing', bumper: 'boing', slingshot: 'chicken-squawk',
  target: 'cat-meow', rollover: 'lane-ping', ramp: 'slide-whistle', plunger: 'boing',
  nudge: 'dog-bark', drain: 'fart-pop', 'ball-save': 'cat-purr', tilt: 'cat-hiss',
  'game-over': 'fart-pop', jackpot: 'chicken-squawk', 'prop-appear': 'cat-meow',
  'prop-destroy': 'boing',
};

const sillyMap: Record<TableSoundEvent, SoundEffectId> = {
  'game-start': 'dog-bark', flipper: 'boing', bumper: 'fart-pop', slingshot: 'chicken-squawk',
  target: 'dog-bark', rollover: 'cat-meow', ramp: 'slide-whistle', plunger: 'boing',
  nudge: 'cat-hiss', drain: 'fart-pop', 'ball-save': 'cat-purr', tilt: 'cat-hiss',
  'game-over': 'chicken-squawk', jackpot: 'slide-whistle', 'prop-appear': 'cat-meow',
  'prop-destroy': 'dog-bark',
};

export function defaultSoundMap(packId: string | undefined): Record<TableSoundEvent, SoundEffectId> {
  if (packId === 'silly') return sillyMap;
  const pack = getSoundPack(packId);
  if (pack.character === 'cartoon') return cartoonMap;
  if (pack.character === 'electronic') return electronicMap;
  if (pack.character === 'fantasy' || pack.character === 'casino') return fantasyMap;
  return classicMap;
}

export function resolveSoundEffect(
  table: TableDefinition,
  event: TableSoundEvent,
  partId?: string,
): SoundEffectId {
  const partOverride = partId ? table.parts.find((part) => part.id === partId)?.soundRef : undefined;
  const candidate = partOverride ?? table.theme.soundEventMap?.[event];
  if (candidate && SOUND_EFFECTS.some((effect) => effect.id === candidate)) return candidate as SoundEffectId;
  return defaultSoundMap(table.theme.soundPackId)[event];
}

const builtInPartSoundOverrides: Record<string, Partial<Record<string, SoundEffectId>>> = {
  'haunted-keep-v1': {
    'bumper-left': 'pop-deep',
    'bumper-center': 'lane-bleep',
    'bumper-right': 'target-knock',
    'sling-left': 'coil-heavy',
    'sling-right': 'rubber-snap',
    'catch-haunted': 'spectral-capture',
  },
  'deep-sea-dive-v1': {
    // Reuse the proven pressure-line clank/hiss synthesis for the decompression
    // catch so capture no longer sounds like another reef bumper impact.
    'catch-deep': 'steam-brake',
  },
};

export function applySoundPack(table: TableDefinition, packId: string): TableDefinition {
  const pack = SOUND_PACKS.find((candidate) => candidate.id === packId) ?? SOUND_PACKS[0]!;
  const partOverrides = builtInPartSoundOverrides[table.id];
  const parts = partOverrides
    ? table.parts.map((part) => ({ ...part, soundRef: partOverrides[part.id] ?? part.soundRef }))
    : table.parts;
  return {
    ...table,
    parts,
    theme: { ...table.theme, soundPackId: pack.id, soundEventMap: { ...defaultSoundMap(pack.id) } },
  };
}

export interface ArtPieceAsset {
  id: string;
  name: string;
  slots: TableArtSlot[];
  description: string;
}

export const ART_PIECES: ArtPieceAsset[] = [
  { id: 'badge-pinball', name: 'Pinball Badge', slots: ['playfield-center', 'apron'], description: 'Classic concentric arcade badge.' },
  { id: 'chevron-double', name: 'Double Chevron', slots: ['playfield-center', 'apron', 'ramp-decal'], description: 'Fast geometric chevrons.' },
  { id: 'reactor-ring', name: 'Reactor Ring', slots: ['playfield-center', 'bumper-cap'], description: 'Concentric sci-fi energy ring.' },
  { id: 'starburst', name: 'Starburst', slots: ['playfield-center', 'bumper-cap', 'target-face'], description: 'Eight-point arcade burst.' },
  { id: 'lightning', name: 'Lightning', slots: ['target-face', 'ramp-decal', 'sling-plastic'], description: 'Angular lightning bolt.' },
  { id: 'speed-stripes', name: 'Speed Stripes', slots: ['target-face', 'ramp-decal', 'sling-plastic'], description: 'Parallel racing stripes.' },
  { id: 'circuit-node', name: 'Circuit Node', slots: ['playfield-center', 'target-face', 'ramp-decal'], description: 'PCB-like trace symbol.' },
  { id: 'crown', name: 'Crown', slots: ['playfield-center', 'apron', 'target-face'], description: 'Royal crown emblem.' },
  { id: 'planet-ring', name: 'Ringed Planet', slots: ['playfield-center', 'bumper-cap'], description: 'Space-themed ringed planet.' },
  { id: 'ghost-eye', name: 'Ghost Eye', slots: ['bumper-cap', 'target-face'], description: 'Spooky circular eye mark.' },
  { id: 'wave-mark', name: 'Wave Mark', slots: ['playfield-center', 'ramp-decal', 'sling-plastic'], description: 'Three flowing wave strokes.' },
  { id: 'pin-paw', name: 'Pin Paw', slots: ['playfield-center', 'apron', 'bumper-cap', 'target-face'], description: 'Mascot paw-print insert motif.' },
  { id: 'pin-claw', name: 'Pin Claw Marks', slots: ['playfield-center', 'apron', 'ramp-decal', 'sling-plastic'], description: 'Mischievous claw-scratch accent.' },
  { id: 'pin-moon', name: 'Pin Moon', slots: ['playfield-center', 'bumper-cap', 'target-face'], description: 'Moonlit Pin theme emblem.' },
  { id: 'blank', name: 'None / Clean', slots: ['playfield-center', 'apron', 'bumper-cap', 'target-face', 'ramp-decal', 'sling-plastic'], description: 'No additional art piece.' },
];

const ART_SLOTS: TableArtSlot[] = ['playfield-center', 'apron', 'bumper-cap', 'target-face', 'ramp-decal', 'sling-plastic'];

function visualDefaults(presetId: string): Record<TableArtSlot, string> {
  if (presetId.includes('neon') || presetId.includes('quantum') || presetId.includes('synthwave')) {
    return { 'playfield-center': 'reactor-ring', apron: 'chevron-double', 'bumper-cap': 'reactor-ring', 'target-face': 'lightning', 'ramp-decal': 'circuit-node', 'sling-plastic': 'lightning' };
  }
  if (presetId.includes('cosmic')) {
    return { 'playfield-center': 'planet-ring', apron: 'chevron-double', 'bumper-cap': 'planet-ring', 'target-face': 'starburst', 'ramp-decal': 'speed-stripes', 'sling-plastic': 'starburst' };
  }
  if (presetId.includes('haunted')) {
    return { 'playfield-center': 'ghost-eye', apron: 'badge-pinball', 'bumper-cap': 'ghost-eye', 'target-face': 'ghost-eye', 'ramp-decal': 'wave-mark', 'sling-plastic': 'wave-mark' };
  }
  if (presetId.includes('royal') || presetId.includes('jungle')) {
    return { 'playfield-center': 'crown', apron: 'crown', 'bumper-cap': 'starburst', 'target-face': 'crown', 'ramp-decal': 'speed-stripes', 'sling-plastic': 'starburst' };
  }
  if (presetId.includes('ocean') || presetId.includes('glacier')) {
    return { 'playfield-center': 'wave-mark', apron: 'chevron-double', 'bumper-cap': 'reactor-ring', 'target-face': 'wave-mark', 'ramp-decal': 'wave-mark', 'sling-plastic': 'wave-mark' };
  }
  return { 'playfield-center': 'badge-pinball', apron: 'chevron-double', 'bumper-cap': 'starburst', 'target-face': 'speed-stripes', 'ramp-decal': 'speed-stripes', 'sling-plastic': 'chevron-double' };
}

export const VISUAL_PACKS = THEME_PRESETS.map((preset) => ({ id: preset.id, name: preset.name, description: preset.description }));

export function applyVisualPack(table: TableDefinition, presetId: string): TableDefinition {
  const themed = applyThemePreset(table, presetId);
  return {
    ...themed,
    theme: { ...themed.theme, visualPackId: themed.theme.presetId, artSlots: visualDefaults(themed.theme.presetId ?? presetId) },
  };
}

export function artChoicesForSlot(slot: TableArtSlot): ArtPieceAsset[] {
  return ART_PIECES.filter((piece) => piece.slots.includes(slot));
}

export function setArtSlot(table: TableDefinition, slot: TableArtSlot, artId: string): TableDefinition {
  if (!ART_PIECES.some((piece) => piece.id === artId && piece.slots.includes(slot))) return table;
  return { ...table, theme: { ...table.theme, artSlots: { ...table.theme.artSlots, [slot]: artId } } };
}

export function setSoundEvent(table: TableDefinition, event: TableSoundEvent, effectId: SoundEffectId): TableDefinition {
  if (!SOUND_EFFECTS.some((effect) => effect.id === effectId)) return table;
  return { ...table, theme: { ...table.theme, soundEventMap: { ...table.theme.soundEventMap, [event]: effectId } } };
}

export const SOUND_EVENT_ORDER: TableSoundEvent[] = [
  'game-start', 'flipper', 'plunger', 'bumper', 'slingshot', 'target', 'rollover',
  'ramp', 'prop-appear', 'prop-destroy', 'nudge', 'drain', 'ball-save', 'tilt',
  'game-over', 'jackpot',
];

export const ART_SLOT_ORDER = ART_SLOTS;
